import type {
  Assigner,
  EventObject,
  GuardFn,
  State,
  StateMachine,
  TransitionConfig,
} from './types';

function toArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function applyAssigners<C, E extends EventObject>(
  context: C,
  event: E,
  actions: Assigner<C, E> | Assigner<C, E>[] | undefined
): C {
  return toArray(actions).reduce<C>(
    (ctx, action) => ({ ...ctx, ...action.assignment(ctx, event) }),
    context
  );
}

/** Build a snapshot for `value`, wiring `done` and `matches`. */
export function makeState<C, V extends string>(
  machine: StateMachine<C, V>,
  value: V,
  context: C
): State<C, V> {
  return {
    value,
    context,
    done: machine.states[value]?.type === 'final',
    matches: (candidate: V) => candidate === value,
    can: (event: string) => Boolean(machine.states[value]?.on?.[event]),
  };
}

/** The machine's initial snapshot, optionally seeded with `input`. */
export function initialState<C, V extends string>(
  machine: StateMachine<C, V>,
  input?: Partial<C>
): State<C, V> {
  const context = input ? { ...machine.context, ...input } : machine.context;
  return makeState(machine, machine.initial, context);
}

type Resolved<C, E extends EventObject> =
  | { taken: false }
  | { taken: true; target?: string; actions?: Assigner<C, E> | Assigner<C, E>[] };

function resolve<C, E extends EventObject>(
  config: TransitionConfig<C, E> | undefined,
  context: C,
  event: E,
  guards?: Record<string, GuardFn<C, E>>
): Resolved<C, E> {
  if (config === undefined) return { taken: false };
  if (typeof config === 'string') return { taken: true, target: config };
  if (config.guard) {
    const guard = guards?.[config.guard];
    if (!guard) {
      throw new Error(`[@heidi/flow] Guard "${config.guard}" has no implementation.`);
    }
    if (!guard(context, event)) return { taken: false };
  }
  return { taken: true, target: config.target, actions: config.actions };
}

/**
 * Apply an explicit transition config to a state. Shared by `on` handlers and
 * by invoke `onDone`/`onError`. Returns the same state (no-op) when the config
 * is absent or a guard rejects.
 */
export function applyTransitionConfig<C, V extends string, E extends EventObject>(
  machine: StateMachine<C, V>,
  state: State<C, V>,
  config: TransitionConfig<C, E> | undefined,
  event: E,
  guards?: Record<string, GuardFn<C, E>>
): State<C, V> {
  const resolved = resolve(config, state.context, event, guards);
  if (!resolved.taken) return state;
  const nextValue = (resolved.target ?? state.value) as V;
  const nextContext = applyAssigners(state.context, event, resolved.actions);
  return makeState(machine, nextValue, nextContext);
}

/**
 * Pure reducer: given a state and an event, return the next state. Returns the
 * *same* state reference on an unhandled event or a rejected guard (no-op).
 */
export function transition<C, V extends string, E extends EventObject>(
  machine: StateMachine<C, V>,
  state: State<C, V>,
  event: E,
  impls?: { guards?: Record<string, GuardFn<C, E>> }
): State<C, V> {
  const node = machine.states[state.value];
  const config = node?.on?.[event.type];
  if (config === undefined) return state;
  return applyTransitionConfig(machine, state, config, event, impls?.guards);
}
